/**
 * GMA-1616 (CompassCare Web 1/14 — Foundation) — left icon sidebar.
 *
 * Renders the facelift's primary navigation from an injected `sections` list
 * (the app shell passes WEB_NAV_SECTIONS from webNavModel). Chrome colors come
 * from the single design-token set — no per-page palette.
 *
 * Accessibility (GMA-1616 AC): a labelled <nav> landmark; every item is a
 * button with the icon decorative (aria-hidden) and a text label always shown;
 * the active item carries aria-current="page"; 44px minimum hit targets.
 */
import React from "react";
import PropTypes from "prop-types";
import Dashboard from "@mui/icons-material/Dashboard";
import MonitorHeart from "@mui/icons-material/MonitorHeart";
import Groups from "@mui/icons-material/Groups";
import NotificationsActive from "@mui/icons-material/NotificationsActive";
import Map from "@mui/icons-material/Map";
import CallSplit from "@mui/icons-material/CallSplit";
import Sensors from "@mui/icons-material/Sensors";
import Assessment from "@mui/icons-material/Assessment";
import Insights from "@mui/icons-material/Insights";
import Forum from "@mui/icons-material/Forum";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import History from "@mui/icons-material/History";
import Settings from "@mui/icons-material/Settings";
import Circle from "@mui/icons-material/Circle";
import { theme } from "../../styles/designTokens";

// Named icons used by the 13 facelift sections. Explicit imports keep the
// bundle lean (vs. `import * as Icons`, which pulls the entire icon set).
const DEFAULT_ICON_MAP = {
  Dashboard,
  MonitorHeart,
  Groups,
  NotificationsActive,
  Map,
  CallSplit,
  Sensors,
  Assessment,
  Insights,
  Forum,
  CalendarMonth,
  History,
  Settings,
};

export default function Sidebar({
  sections = [],
  activeKey,
  onNavigate,
  iconMap = DEFAULT_ICON_MAP,
}) {
  // Defaults only apply to `undefined`; the app shell injects these props, so
  // guard against an explicit `null` (or a non-array `sections`) to avoid a
  // render-time crash during initial integration.
  const safeSections = Array.isArray(sections) ? sections : [];
  const icons = iconMap || DEFAULT_ICON_MAP;
  // Without a navigate handler each item would be an enabled but inert button —
  // focusable/activatable yet doing nothing, misleading keyboard/SR users.
  // Disable the items until navigation is wired.
  const navigable = typeof onNavigate === "function";
  return (
    <nav
      aria-label="Primary"
      style={{
        backgroundColor: theme.bg,
        borderRight: `1px solid ${theme.border}`,
        fontFamily: theme.fontUI,
        width: 220,
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <ul style={{ listStyle: "none", margin: 0, padding: "8px 0" }}>
        {safeSections.map((section) => {
          const Icon = icons[section.icon] || Circle;
          const active = section.key === activeKey;
          return (
            <li key={section.key}>
              <button
                type="button"
                aria-current={active ? "page" : undefined}
                disabled={!navigable}
                onClick={navigable ? () => onNavigate(section) : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  minHeight: 44,
                  padding: "0 16px",
                  border: "none",
                  cursor: navigable ? "pointer" : "default",
                  textAlign: "left",
                  background: active ? theme.surface : "transparent",
                  // Chrome text from the single token set — no hardcoded hexes.
                  color: active ? theme.textPrimary : theme.textMuted,
                  fontFamily: theme.fontUI,
                  fontSize: 14,
                }}
              >
                <Icon aria-hidden="true" fontSize="small" />
                <span>{section.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

Sidebar.propTypes = {
  /** Ordered nav sections ({key,label,path,icon}); app shell passes WEB_NAV_SECTIONS. */
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
      icon: PropTypes.string,
    }),
  ),
  /** Key of the active section (aria-current="page"). */
  activeKey: PropTypes.string,
  /** Called with the section object when an item is clicked. */
  onNavigate: PropTypes.func,
  /** Icon-name -> component map; defaults to the 13 facelift icons. */
  iconMap: PropTypes.object,
};
